import { get, push, ref, remove, runTransaction, update } from 'firebase/database';
import { database } from '../firebase';

// The public queue contains minimized delivery metadata only. It omits nationality,
// salary, legal evidence, quotas and calculations, but employee IDs and HR change
// categories remain sensitive metadata and must be access-restricted in RTDB rules.
const OUTBOX_NAME = 'nationalizationLifecycleOutbox';
const ALLOWED_CHANGE_KINDS = new Set([
  'lifecycle',
  'occupation',
  'compensation',
  'contract',
  'eligibility'
]);
const activeFlushes = new Map();

const outboxPath = (companyUID) => `companies/${companyUID}/${OUTBOX_NAME}`;

function normalizeChangeKinds(changeKinds) {
  const normalized = Array.from(new Set(
    (Array.isArray(changeKinds) && changeKinds.length ? changeKinds : ['lifecycle'])
      .map((value) => String(value || '').trim())
      .filter((value) => ALLOWED_CHANGE_KINDS.has(value))
  ));
  return normalized.length ? normalized : ['lifecycle'];
}

export function createNationalizationLifecycleEvent({
  companyUID,
  employeeId,
  source = 'employee_lifecycle',
  changeKinds = ['lifecycle'],
  establishmentId
}) {
  if (!companyUID || !employeeId) {
    throw new Error('A company and employee are required for lifecycle synchronization.');
  }
  // A Firebase push key is unique, path-safe, and remains stable across every
  // retry. Calling push() allocates the key locally; no write happens here.
  const eventId = push(ref(database, outboxPath(companyUID))).key;
  if (!eventId) throw new Error('Could not allocate a lifecycle synchronization event.');
  const record = {
    schemaVersion: 1,
    employeeId: String(employeeId),
    source: String(source || 'employee_lifecycle').slice(0, 80),
    changeKinds: normalizeChangeKinds(changeKinds),
    queuedAt: Date.now(),
    attempts: 0
  };
  // A legal-establishment assignment is needed only when a profile contains
  // multiple branches. It is bound to the idempotency record and validated by
  // the server; wage, nationality and evidence never enter this public marker.
  if (String(establishmentId || '').trim()) {
    record.establishmentId = String(establishmentId).trim();
  }
  return {
    eventId,
    path: `${outboxPath(companyUID)}/${eventId}`,
    record
  };
}

export function addNationalizationLifecycleEventToUpdates(updates, options) {
  const event = createNationalizationLifecycleEvent(options);
  // Callers put this into the same root Firebase update as the employee change.
  // Therefore a successful hire/offboard/role edit can never exist without its
  // corresponding retry marker.
  updates[event.path] = event.record;
  return event;
}

export async function enqueueNationalizationLifecycleEvent(options) {
  const updates = {};
  const event = addNationalizationLifecycleEventToUpdates(updates, options);
  await update(ref(database), updates);
  return event;
}

export async function removeNationalizationLifecycleEvent(companyUID, eventId) {
  if (!companyUID || !eventId) return;
  await remove(ref(database, `${outboxPath(companyUID)}/${eventId}`));
}

async function markAttemptFailed(companyUID, eventId, record, error) {
  const attempts = Number(record?.attempts || 0) + 1;
  // Keep retrying, but back off persistent setup/validation failures to avoid
  // hammering the API. A later corrected profile/employee edit will still replay.
  const delayMs = Math.min(60 * 60 * 1000, 5000 * (2 ** Math.min(attempts - 1, 9)));
  const attemptAt = Date.now();
  await runTransaction(
    ref(database, `${outboxPath(companyUID)}/${eventId}`),
    (current) => {
      // Another tab may have completed delivery and the backend may already have
      // removed this marker after our GET. Returning undefined aborts instead of
      // recreating a stale/partial event from this losing tab.
      if (!current || typeof current !== 'object') return undefined;
      return {
        ...current,
        attempts,
        lastAttemptAt: attemptAt,
        nextAttemptAt: attemptAt + delayMs,
        lastStatus: Number(error?.status || 0) || 'network_error'
      };
    },
    { applyLocally: false }
  );
}

export async function flushNationalizationLifecycleOutbox(companyUID, deliver, { limit = 100 } = {}) {
  if (!companyUID || typeof deliver !== 'function') return { delivered: 0, pending: 0 };
  if (activeFlushes.has(companyUID)) return activeFlushes.get(companyUID);

  const operation = (async () => {
    const snapshot = await get(ref(database, outboxPath(companyUID)));
    const raw = snapshot.val();
    if (!raw || typeof raw !== 'object') return { delivered: 0, pending: 0 };

    const now = Date.now();
    const entries = Object.entries(raw)
      .filter(([, record]) => record && typeof record === 'object')
      .sort(([, left], [, right]) => Number(left.queuedAt || 0) - Number(right.queuedAt || 0));
    // Apply the delivery limit only after excluding delayed and malformed rows.
    // Otherwise one old poison/delayed marker can indefinitely starve every newer
    // ready workforce event behind it.
    const deliveryEntries = entries
      .filter(([, record]) => (
        Boolean(record.employeeId)
        && Number(record.nextAttemptAt || 0) <= now
      ))
      .slice(0, Math.max(1, limit));
    let delivered = 0;
    // Delayed/malformed rows and ready rows beyond this batch remain pending.
    let pending = entries.length - deliveryEntries.length;

    for (const [eventId, record] of deliveryEntries) {
      try {
        await deliver({
          eventId,
          employeeId: String(record.employeeId),
          source: String(record.source || 'employee_lifecycle'),
          changeKinds: normalizeChangeKinds(record.changeKinds),
          establishmentId: String(record.establishmentId || '').trim() || undefined
        });
        // The backend also removes this marker. This duplicate cleanup covers a
        // deployment where backend cleanup was interrupted after committing.
        await removeNationalizationLifecycleEvent(companyUID, eventId);
        delivered += 1;
      } catch (error) {
        pending += 1;
        // Authentication/authorization failures describe this browser user, not
        // the shared event. Do not make an unauthorized tab delay retries for an
        // owner or manager who can legitimately deliver the same marker.
        if (error?.status === 401 || error?.status === 403) {
          continue;
        }
        try {
          await markAttemptFailed(companyUID, eventId, record, error);
        } catch (writeError) {
          // The original marker remains durable even if attempt metadata cannot
          // be updated, so a future online/visibility replay can try again.
          console.warn('Could not update nationalization lifecycle retry metadata:', writeError);
        }
      }
    }
    return { delivered, pending };
  })();

  activeFlushes.set(companyUID, operation);
  try {
    return await operation;
  } finally {
    activeFlushes.delete(companyUID);
  }
}

const nationalizationLifecycleOutbox = {
  addNationalizationLifecycleEventToUpdates,
  createNationalizationLifecycleEvent,
  enqueueNationalizationLifecycleEvent,
  flushNationalizationLifecycleOutbox,
  removeNationalizationLifecycleEvent
};

export default nationalizationLifecycleOutbox;
